import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { api, formatError } from "@/lib/api";
import { cartRequestItem, cartStockLimit } from "@/lib/cartInventory";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { trackMetaAddToCart } from "@/lib/analytics";

const CartCtx = createContext(null);
const KEY = "prk_cart_v1";

const readGuestCart = () => {
  try {
    const storedCart = JSON.parse(localStorage.getItem(KEY)) || [];
    return Array.isArray(storedCart) ? storedCart : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const auth = useAuth?.() || null;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const syncTimer = useRef(null);
  const itemsRef = useRef([]);
  const versionRef = useRef(0);
  const channelRef = useRef(null);
  const userId = auth?.user?.id;

  const applyServerCart = useCallback((cart, preservePendingSync = false) => {
    if (!preservePendingSync && syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    const nextItems = Array.isArray(cart?.items) ? cart.items : [];
    itemsRef.current = nextItems;
    versionRef.current = Number(cart?.version || 0);
    setItems(nextItems);
    setReady(true);
  }, []);

  const refreshCart = useCallback(async () => {
    if (!userId) {
      return { items: [], version: 0 };
    }
    const response = await api.get("/me/cart");
    applyServerCart(response.data);
    return response.data;
  }, [applyServerCart, userId]);

  const announceCartChange = useCallback(() => {
    channelRef.current?.postMessage({ type: "cart-updated" });
  }, []);

  const mergeGuestCart = useCallback(async (serverCart, guestItems) => {
    let currentCart = serverCart;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await api.post("/me/cart/sync", {
          items: [
            ...(currentCart.items || []).map(cartRequestItem),
            ...guestItems.map(cartRequestItem),
          ],
          version: Number(currentCart.version || 0),
        });
        return response.data.cart;
      } catch (error) {
        const latestCart = error?.response?.data?.detail?.cart;
        if (!latestCart || attempt === 1) {
          throw error;
        }
        currentCart = latestCart;
      }
    }
    return currentCart;
  }, []);

  const syncItems = useCallback(async (nextItems) => {
    if (!userId) {
      return;
    }
    try {
      const expectedVersion = versionRef.current;
      const response = await api.post("/me/cart/sync", {
        items: nextItems,
        version: expectedVersion,
      });
      const stockAdjustments = response.data.stock_adjustments || [];
      stockAdjustments.forEach((adjustment) => {
        toast.error(`Only ${adjustment.available_stock} left in stock`);
      });
      if (itemsRef.current === nextItems) {
        applyServerCart(response.data.cart);
      } else {
        versionRef.current = Number(response.data.cart?.version || expectedVersion);
      }
      announceCartChange();
    } catch (error) {
      const currentCart = error?.response?.data?.detail?.cart;
      if (currentCart) {
        applyServerCart(currentCart);
        (currentCart.stock_adjustments || []).forEach((adjustment) => {
          toast.error(`Only ${adjustment.available_stock} left in stock`);
        });
      } else {
        await refreshCart().catch(() => {});
      }
    }
  }, [announceCartChange, applyServerCart, refreshCart, userId]);

  const scheduleSync = useCallback((nextItems) => {
    if (!userId) {
      return;
    }
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      syncTimer.current = null;
      syncItems(nextItems);
    }, 350);
  }, [syncItems, userId]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (auth?.checking) {
      setReady(false);
      return undefined;
    }
    clearTimeout(syncTimer.current);
    if (!userId) {
      const guestCart = readGuestCart();
      itemsRef.current = guestCart;
      setItems(guestCart);
      versionRef.current = 0;
      setReady(true);
      return undefined;
    }
    const storedGuestCart = readGuestCart();
    const guestCart = itemsRef.current.length ? itemsRef.current : storedGuestCart;
    let active = true;
    const loadServerCart = async () => {
      try {
        const response = await api.get("/me/cart");
        let nextCart = response.data;
        if (guestCart.length) {
          try {
            nextCart = await mergeGuestCart(response.data, guestCart);
            localStorage.removeItem(KEY);
            (nextCart.stock_adjustments || []).forEach((adjustment) => {
              toast.error(`Only ${adjustment.available_stock} left in stock`);
            });
            announceCartChange();
          } catch (error) {
            toast.error("Your guest cart could not merge yet. Please try again.");
          }
        }
        if (active) {
          applyServerCart(nextCart);
        }
      } catch {
        if (active) {
          applyServerCart({ items: [], version: 0 });
        }
      }
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible" && !syncTimer.current) {
        refreshCart().catch(() => {});
      }
    };
    const channel = typeof BroadcastChannel === "undefined"
      ? null
      : new BroadcastChannel("eira-cart");
    channelRef.current = channel;
    if (channel) {
      channel.onmessage = (event) => {
        if (event.data?.type === "cart-updated" && !syncTimer.current) {
          refreshCart().catch(() => {});
        }
      };
    }
    loadServerCart();
    window.addEventListener("focus", refreshOnVisibility);
    document.addEventListener("visibilitychange", refreshOnVisibility);
    const poll = window.setInterval(refreshOnVisibility, 5000);
    return () => {
      active = false;
      clearTimeout(syncTimer.current);
      window.clearInterval(poll);
      window.removeEventListener("focus", refreshOnVisibility);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
      channel?.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [announceCartChange, applyServerCart, auth?.checking, mergeGuestCart, refreshCart, userId]);

  useEffect(() => {
    if (!auth?.checking && !userId && ready) {
      localStorage.setItem(KEY, JSON.stringify(itemsRef.current));
    }
  }, [auth?.checking, items, ready, userId]);

  const key = (p, v) => `${p}::${v?.sku || `${v?.label || ""}::${v?.material || ""}`}`;

  const updateItems = (updater) => {
    setItems((previousItems) => {
      const nextItems = updater(previousItems);
      itemsRef.current = nextItems;
      scheduleSync(nextItems);
      return nextItems;
    });
  };

  const add = (product, variant, qty = 1) => {
    if (product.product_type === "gift_box") {
      toast.error("Choose your gift box contents on the product page first");
      return false;
    }
    const k = key(product.id, variant);
    const found = itemsRef.current.find((item) => item._k === k);
    const stock = Number(variant?.stock ?? product.stock) || 0;
    const line = {
      _k: k, product_id: product.id, name: product.name, product_type: "standard",
      food_symbol: product.food_symbol, image: product.images?.[0],
      variant_label: variant?.label || null, variant_material: variant?.material || null,
      variant_sku: variant?.sku || null, price: variant?.price ?? product.price,
      mrp: variant?.mrp ?? product.mrp, available_stock: stock,
      inventory_items: [{ product_id: product.id, variant_label: variant?.label || null, variant_material: variant?.material || null, variant_sku: variant?.sku || null, quantity: 1, available_stock: stock }],
    };
    const cap = cartStockLimit(line, itemsRef.current);
    const requested = (found?.quantity || 0) + qty;
    if (!Number.isInteger(qty) || qty < 1 || requested > cap) {
      toast.error(`Only ${cap} available with the items in your bag`);
      return false;
    }
    updateItems((prev) => found ? prev.map((item) => item._k === k ? { ...line, quantity: requested } : item) : [...prev, { ...line, quantity: requested }]);
    trackMetaAddToCart({
      id: product.id,
      name: product.name,
      value: line.price,
      quantity: qty,
    });
    return true;
  };

  const addHamper = async (product, selections, quantity) => {
    try {
      const { data } = await api.post("/cart/quote", { items: [
        ...itemsRef.current.map(cartRequestItem),
        { product_id: product.id, quantity, hamper_selections: selections },
      ] });
      updateItems(() => data.items);
      trackMetaAddToCart({
        id: product.id,
        name: product.name,
        value: product.price,
        quantity,
      });
      toast.success("Gift box added to cart", { duration: 2500 });
      return true;
    } catch (error) {
      toast.error(formatError(error));
      return false;
    }
  };

  const addMany = (newItems) => {
    updateItems((prev) => newItems.reduce((nextItems, item) => {
      const found = nextItems.find((cartItem) => cartItem._k === item._k);
      if (found) {
        return nextItems.map((cartItem) => (
          cartItem._k === item._k
            ? { ...cartItem, quantity: Math.min(cartStockLimit(cartItem, nextItems), cartItem.quantity + item.quantity) }
            : cartItem
        ));
      }
      const cap = cartStockLimit(item, nextItems);
      return cap > 0 ? [...nextItems, { ...item, quantity: Math.min(cap, item.quantity) }] : nextItems;
    }, prev));
  };

  const remove = (k) => updateItems((prev) => prev.filter((i) => i._k !== k));
  const setQty = (k, q) => {
    const item = itemsRef.current.find((cartItem) => cartItem._k === k);
    if (!item) return;
    if (!Number.isInteger(q)) return;
    const maximumAllowed = cartStockLimit(item, itemsRef.current);
    const nextQuantity = Math.min(q, maximumAllowed);
    if (q > maximumAllowed) {
      toast.error(`Only ${maximumAllowed} available with the items in your bag`);
    }
    updateItems((prev) => prev.flatMap((cartItem) => (
      cartItem._k !== k
        ? [cartItem]
        : nextQuantity <= 0
          ? []
          : [{ ...cartItem, quantity: nextQuantity }]
    )));
  };
  const clear = () => updateItems(() => []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider
      value={{
        items,
        add,
        addHamper,
        addMany,
        remove,
        setQty,
        clear,
        subtotal,
        count,
        open,
        setOpen,
        ready,
        refreshCart,
        applyServerCart,
      }}
    >
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
